import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, MapPin, Clock, ShieldCheck, GraduationCap, Heart, Award, Upload } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const POSITIONS = [
  { title: "Senior Site Engineer", dept: "Contracting", location: "Riyadh", type: "Full-time", featured: true, summary: "Lead civil works on flagship PIF tower project. 10+ years experience required." },
  { title: "Electro Mechanical Foreman", dept: "MEP", location: "Jeddah", type: "Full-time", summary: "HVAC + power distribution lead for hospital fit-out projects." },
  { title: "HSE Officer (NEBOSH)", dept: "HSE", location: "Dammam", type: "Full-time", summary: "Drive zero-incident culture across Aramco refinery contracts." },
  { title: "Project Manager — Airport Ground Services", dept: "Aviation", location: "Jeddah (KAIA)", type: "Full-time", summary: "Manage ramp services, GSE operations and 24/7 turnaround SLAs." },
  { title: "Quantity Surveyor", dept: "Commercial", location: "Riyadh", type: "Full-time", summary: "BOQ, valuations, claims management for projects above SAR 50M." },
  { title: "Cleaning Operations Supervisor", dept: "FM", location: "Multiple", type: "Full-time", summary: "Lead daily janitorial teams across malls, hospitals, and labour camps." },
  { title: "Landscape Architect", dept: "Landscaping", location: "Tabuk (NEOM)", type: "Contract", summary: "Hardscape + softscape design for compounds and corporate campuses." },
  { title: "Procurement Officer", dept: "Procurement", location: "Jeddah HQ", type: "Full-time", summary: "Vendor management, RFQ negotiation, supply chain optimisation." },
];

const BENEFITS = [
  { icon: ShieldCheck, title: "World-class HSE", desc: "NEBOSH-led safety culture, mandatory PPE, zero-tolerance for unsafe acts." },
  { icon: GraduationCap, title: "Training & Certification", desc: "OSHA, IOSH, IPAF and trade-specific certifications fully sponsored." },
  { icon: Heart, title: "Employee Wellbeing", desc: "Medical insurance, accommodation, transport, end-of-service benefits." },
  { icon: Award, title: "Vision 2030 Saudization", desc: "Active career paths for Saudi nationals across engineering & operations." },
];

const empty = {
  full_name: "",
  email: "",
  phone: "",
  position: "",
  experience_years: "",
  nationality: "",
  cv_link: "",
  message: "",
};

export default function CareersPage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const departments = ["All", ...Array.from(new Set(POSITIONS.map((p) => p.dept)))];
  const filtered = filter === "All" ? POSITIONS : POSITIONS.filter((p) => p.dept === filter);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.position) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/careers", { ...form, experience_years: Number(form.experience_years) || 0 });
      toast.success("Application received. HR will review and respond within 7 business days.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const applyTo = (title) => {
    update("position", title);
    document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="careers-page">
      <TopBar />
      <Navbar />

      {/* Hero */}
      <section className="bg-secondary text-secondary-foreground grain pt-32 pb-20 relative overflow-hidden">
        <div className="container-page">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-secondary-foreground/60 hover:text-primary transition-colors" data-testid="careers-back-home">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="overline text-primary">/ Join MNH</span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05]">
                Build the Kingdom <br />
                <span className="text-primary">with us.</span>
              </h1>
            </div>
            <p className="lg:col-span-4 text-secondary-foreground/65 leading-relaxed">
              Join 1,000+ professionals delivering Saudi Arabia's most demanding contracting and aviation projects. We hire across all skill levels — engineering, supervision, trades, and support.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="container-page py-20">
        <span className="overline">/ Why MNH</span>
        <h2 className="mt-4 text-3xl sm:text-4xl font-black mb-10">More than a job. A career.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="careers-benefits">
          {BENEFITS.map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.07 }} className="bg-card border border-border p-6 hover:border-primary transition-colors group" data-testid={`career-benefit-${i}`}>
              <div className="h-11 w-11 bg-secondary text-primary flex items-center justify-center"><b.icon className="h-5 w-5" /></div>
              <h3 className="mt-5 text-lg font-bold">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              <div className="mt-5 h-px w-8 bg-primary group-hover:w-20 transition-all duration-500" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Open positions */}
      <section className="bg-muted/40 border-y border-border py-20">
        <div className="container-page">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <span className="overline">/ Open Positions</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black">{POSITIONS.length} roles, across {departments.length - 1} departments.</h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="careers-filter-bar">
              {departments.map((d) => (
                <button
                  key={d}
                  onClick={() => setFilter(d)}
                  className={`text-xs uppercase tracking-[0.18em] px-3 py-2 border transition-colors ${
                    filter === d ? "bg-secondary text-secondary-foreground border-secondary" : "border-border text-foreground/70 hover:border-primary hover:text-primary"
                  }`}
                  data-testid={`careers-filter-${d}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3" data-testid="careers-positions">
            {filtered.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.4, delay: i * 0.04 }} className="bg-card border border-border p-6 sm:p-7 flex flex-wrap gap-4 items-center justify-between hover:border-primary transition-colors group" data-testid={`career-position-${i}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg sm:text-xl font-bold">{p.title}</h3>
                    {p.featured && <Badge className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">Featured</Badge>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground uppercase tracking-[0.16em]">
                    <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{p.dept}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{p.type}</span>
                  </div>
                  <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{p.summary}</p>
                </div>
                <Button onClick={() => applyTo(p.title)} className="rounded-sm bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`career-apply-${i}`}>
                  Apply Now
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply-form" className="container-page py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline">/ Apply</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black leading-tight">
              Send us your <span className="text-primary">application.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Upload your CV to a public link (Google Drive, Dropbox) and share it below. Our HR team will review and respond within 7 business days.
            </p>
            <div className="mt-8 p-5 border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 text-foreground font-semibold">
                <Upload className="h-4 w-4 text-primary" /> CV Submission Tip
              </div>
              <p className="mt-2 leading-relaxed">
                Upload your CV to Google Drive → "Share" → "Anyone with the link" → paste the link in the field below.
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-secondary text-secondary-foreground p-8 sm:p-10 grain space-y-5" data-testid="careers-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <FormField label="Full Name *">
                <Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="career-name" required />
              </FormField>
              <FormField label="Nationality">
                <Input value={form.nationality} onChange={(e) => update("nationality", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="career-nationality" />
              </FormField>
              <FormField label="Email *">
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="career-email" required />
              </FormField>
              <FormField label="Phone *">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="career-phone" required />
              </FormField>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <FormField label="Position *">
                <Select value={form.position} onValueChange={(v) => update("position", v)}>
                  <SelectTrigger className="bg-transparent border-white/15 text-white rounded-sm h-11 data-[placeholder]:text-white/40" data-testid="career-position"><SelectValue placeholder="Select position" /></SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map((p, i) => (<SelectItem key={i} value={p.title}>{p.title}</SelectItem>))}
                    <SelectItem value="Open Application">Open Application</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Years of Experience">
                <Input type="number" min="0" value={form.experience_years} onChange={(e) => update("experience_years", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="career-exp" />
              </FormField>
            </div>
            <FormField label="CV Link (Google Drive / Dropbox)">
              <Input value={form.cv_link} onChange={(e) => update("cv_link", e.target.value)} placeholder="https://drive.google.com/..." className="bg-transparent border-white/15 text-white rounded-sm h-11" data-testid="career-cv" />
            </FormField>
            <FormField label="Cover Note">
              <Textarea rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className="bg-transparent border-white/15 text-white rounded-sm" placeholder="Tell us why you'd like to join MNH." data-testid="career-message" />
            </FormField>
            <Button type="submit" disabled={loading} size="lg" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12" data-testid="career-submit">
              {loading ? "Submitting…" : "Submit Application"}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-secondary-foreground/80 mb-2 block text-xs uppercase tracking-[0.2em]">{label}</Label>
      {children}
    </div>
  );
}
